import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useRef, useState, useEffect } from "react";

import { setUserDetails, toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { Loader } from "components/Loader";
import { initialValues, validationSchema } from "../../validation";
import {
  CloseIcon,
  // ConnectButton,
  PenIcon,
  // ProfileDescription,
  ProfileFieldContent,
  ProfileFieldWrapper,
  ProfileLabel,
  ProfileModalContainer,
  // SocialActionWrapper,
  // SocialDeleteButton,
  // SocialFieldContent,
  // SocialInfoWrapper,
  // SocialItemWrapper,
  // SocialName,
  // SocialStatus,
  // SocialTitleWrapper,
  // TrashIcon,
  VerifiedFieldContent,
} from "./ProfileModal.styles";
import { userDetailSelect } from "Layout/slice/selector";
import {
  updateProfile,
  uploadUserIcon,
  resendVerification,
} from "../../service";
import { Input } from "components/Form";
import { Button } from "components/Button/index";
import { resendEmailLink } from "features/Auth/service";
import { trimmedName } from "utils/index";
import { ChangePhone } from "../Components/ChangePhone";
import {
  // InstagramIcon,
  LightBulbIcon,
  // LinkInIcon,
  // TwitterIcon,
  VerificationIcon,
} from "assets/icons/index";

export const ProfileModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const user = useSelector(userDetailSelect);
  const fileUploader = useRef(null);

  const [isImageLoading, setIsImageLoading] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(true);
  const [editPhoneVisible, setEditPhoneVisible] = useState(false);

  useEffect(() => {
    if (!user.phone && !user?.phone_verified) {
      setEditPhoneVisible(true);
    }
  }, [user]);

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        social_links: {
          linkedin: values.linkedin,
          twitter: values.twitter,
          instagram: values.instagram || "",
        },
      };

      delete payload.linkedin;
      delete payload.twitter;
      delete payload.instagram;

      await updateProfile(payload);
      dispatch(
        toast({
          body: "Profile has been updated successfully",
        })
      );
      dispatch(
        setUserDetails({
          ...user,
          ...values,
          social_links: {
            ...user.social_links,
            linkedin: values.linkedin,
            twitter: values.twitter,
            instagram: values.instagram || user.social_links?.instagram || "",
          },
        })
      );
      onHide();
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    }
  };

  const handleImageUpload = async (e) => {
    try {
      setIsImageLoading(true);
      const data = new FormData();
      data.append("user_icon", e.target.files[0]);
      const response = await uploadUserIcon(data);
      let values = {};
      Object.assign(values, user);
      values.user_icon = response?.image_url;
      await updateProfile(values);
      dispatch(setUserDetails(values));
      dispatch(
        toast({
          type: "info",
          body: "Profile Image uploaded successfully",
        })
      );
    } catch (error) {
      setIsImageLoading(false);
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setIsImageLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    try {
      const response = await resendEmailLink(user.email);
      dispatch(
        toast({
          type: "info",
          body: response.message,
        })
      );
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    }
  };
  const handleVerifyPhone = async () => {
    try {
      const unmaskedPhone = user.phone.replace(/\D/g, "");
      const response = await resendVerification({
        type: "sms",
        phone: `+${unmaskedPhone}`,
      });
      dispatch(
        toast({
          type: "info",
          body: response.message,
        })
      );
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    }
  };

  const handleVerifiedPhone = () => {
    setEditPhoneVisible(false);
  };

  const handleEditPhoneVisible = (e) => {
    e?.stopPropagation();
    setEditPhoneVisible(true);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...initialValues(user),
      instagram: user?.social_links?.instagram || "",
    },
    onSubmit: handleSubmit,
    validationSchema,
  });

  const handleModalClose = () => {
    formik.resetForm();
    onHide();
  };

  // const isInstagramConnected = !user?.social_links?.instagram;

  return (
    <ProfileModalContainer show={show} onHide={handleModalClose} size="lg">
      <div className="gradient-background" />
      <div className="profile-modal-content">
        {/* Header Section  */}
        <div className="modal-header-section">
          {/* Avatar */}
          <div className="avatar-section">
            {isImageLoading ? (
              <div className="avatar-loader">
                <Loader />
              </div>
            ) : (
              <div className="avatar-wrapper">
                {user?.user_icon ? (
                  <img
                    src={user.user_icon}
                    alt="profile"
                    className="avatar-image"
                  />
                ) : (
                  <div className="avatar-placeholder">
                    <span>{trimmedName(user?.first_name)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="header-actions">
              <button
                type="button"
                className="icon-button"
                onClick={() => fileUploader.current?.click()}
              >
                <PenIcon />
              </button>
            </div>

            <input
              ref={fileUploader}
              className="d-none"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
            />
          </div>

          {/* User Info */}
          <div className="user-info-section">
            <div className="user-name">
              {user?.first_name} {user?.last_name}
              {user?.email_verified && <VerificationIcon />}
            </div>
            <div className="user-company">{user?.bio || "Investment corp"}</div>
          </div>

          {/* Info Banner */}
          {showInfoBanner && (
            <div className="info-banner">
              <div className="banner-content">
                <LightBulbIcon />
                <div className="banner-text">
                  <p>
                    Use this space to introduce yourself and give potential
                    clients an idea of what they can expect from you.
                  </p>
                  <a href="#" className="banner-link">
                    More tips for your Profile page
                  </a>
                </div>
              </div>

              <button
                type="button"
                className="banner-close"
                onClick={() => setShowInfoBanner(false)}
              >
                <CloseIcon />
              </button>
            </div>
          )}
        </div>

        {/* Profile Form Fields - Not wrapped in form to avoid nesting with ChangePhone */}
        <div className="profile-form">
          {/* Name Fields */}
          <ProfileFieldWrapper>
            <ProfileLabel>Name</ProfileLabel>
            <ProfileFieldContent>
              <Input
                type="text"
                placeholder="First name"
                isInvalid={
                  formik.touched.first_name && formik.errors.first_name
                }
                {...formik.getFieldProps("first_name")}
              />
              <Input
                type="text"
                placeholder="Last name"
                isInvalid={formik.touched.last_name && formik.errors.last_name}
                {...formik.getFieldProps("last_name")}
              />
            </ProfileFieldContent>
          </ProfileFieldWrapper>

          {/* Biography */}
          <ProfileFieldWrapper>
            <ProfileLabel>Biography</ProfileLabel>
            <ProfileFieldContent>
              <Input
                as="textarea"
                rows={4}
                placeholder="Create a brief bio. If you're an RIA, it will show on the client's side."
                isInvalid={formik.touched.bio && formik.errors.bio}
                {...formik.getFieldProps("bio")}
                className="bio-input"
              />
            </ProfileFieldContent>
          </ProfileFieldWrapper>

          {/* Email */}
          <ProfileFieldWrapper>
            <ProfileLabel>Email</ProfileLabel>
            <VerifiedFieldContent>
              <Input
                type="email"
                placeholder="Email address"
                value={user?.email || ""}
                disabled
                className="email-input"
              />
              {user?.email_verified && (
                <div className="verified-status">
                  <VerificationIcon />
                  <span>Verified</span>
                </div>
              )}

              {!user?.email_verified && (
                <div className="unverified-message">
                  Please verify your email address.{" "}
                  <span className="link" onClick={handleVerifyEmail}>
                    Resend verification email
                  </span>
                </div>
              )}
            </VerifiedFieldContent>
          </ProfileFieldWrapper>

          {/* Phone Number Section - Outside form to avoid nesting */}
          <ProfileFieldWrapper>
            <ProfileLabel>Phone number</ProfileLabel>
            <ProfileFieldContent>
              <ChangePhone
                onSuccess={handleVerifiedPhone}
                handleEditPhoneVisible={handleEditPhoneVisible}
              />

              {!editPhoneVisible && !user?.phone_verified && (
                <span className="app-tooltip">
                  <span className="tooltip-arrow" />
                  {/* <img src={phoneUnverfiedIcon} className="mx-2" /> */}
                  <span className="tooltiptext bottom">
                    Please verify your phone number by verifying a text code.
                    <span className="link" onClick={handleVerifyPhone}>
                      Send text code now
                    </span>{" "}
                  </span>
                </span>
              )}
            </ProfileFieldContent>
          </ProfileFieldWrapper>
          {/* Socials */}
          {/* <ProfileFieldWrapper>
            <SocialTitleWrapper>
              <ProfileLabel>Socials</ProfileLabel>
              <ProfileDescription>
                Update your profile info: name, bio, phone, and social links.
              </ProfileDescription>
            </SocialTitleWrapper>

            <SocialFieldContent>
              <SocialItemWrapper>
                <SocialInfoWrapper>
                  <InstagramIcon />
                  <div>
                    <SocialName>Instagram</SocialName>
                    <SocialStatus>
                      {isInstagramConnected ? "Connected" : "Not connected"}
                    </SocialStatus>
                  </div>
                </SocialInfoWrapper>

                <SocialActionWrapper>
                  <div className="social-action">
                    {isInstagramConnected ? (
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={isInstagramConnected}
                          onChange={(e) => {
                            if (!e.target.checked) {
                              formik.setFieldValue("instagram", "");
                            }
                          }}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    ) : (
                      <ConnectButton onClick={() => {}}>Connect</ConnectButton>
                    )}
                  </div>

                  {isInstagramConnected && (
                    <SocialDeleteButton
                      onClick={() => formik.setFieldValue("instagram", "")}
                    >
                      <TrashIcon />
                    </SocialDeleteButton>
                  )}
                </SocialActionWrapper>
              </SocialItemWrapper>

              <SocialItemWrapper>
                <SocialInfoWrapper>
                  <TwitterIcon />
                  <SocialName>X (formerly Twitter)</SocialName>
                </SocialInfoWrapper>
                <ConnectButton onClick={() => {}}>Connect</ConnectButton>
              </SocialItemWrapper>

              <SocialItemWrapper>
                <SocialInfoWrapper>
                  <LinkInIcon />
                  <SocialName>LinkedIn</SocialName>
                </SocialInfoWrapper>
                <ConnectButton onClick={() => {}}>Connect</ConnectButton>
              </SocialItemWrapper>
            </SocialFieldContent>
          </ProfileFieldWrapper> */}

          {/* Modal Actions */}
          <div className="modal-actions">
            <Button
              type="button"
              variant="secondary"
              size="medium"
              onClick={handleModalClose}
              className="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="medium"
              className="save-btn"
              disabled={formik.isSubmitting}
              onClick={formik.handleSubmit}
            >
              {formik.isSubmitting ? (
                <Loader className="on-btn" />
              ) : (
                "Save changes"
              )}
            </Button>
          </div>
        </div>
      </div>
    </ProfileModalContainer>
  );
};

ProfileModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};
